import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  useTheme,
  Typography,
  IconButton
} from "@mui/material";
import { Edit, Cancel, Save, CloudUpload } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { SaveUserAddressObject } from "../../../redux/user/userSlice";
import { uploadPostPicture } from "../../../redux/post/postSlice";
import { useDispatch, useSelector } from "react-redux";
import FileUploadModel from "../../fileUploadModal";
import ImageSlider from "../ImageSlider";
import EmptyState from "../../shared/EmptyState/EmptyState";
import { getGeoLocationFromAddress } from "../../../utils/locationUtils";
import { useNotification } from "../../shared/NotificationProvider";

const inputSx = {
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.08)",
  borderRadius: "4px",
  fontSize: "0.85rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.6)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
};

const readonlyFieldSx = {
  "& .MuiOutlinedInput-root": {
    pointerEvents: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
  },
};

const labelSx = {
  color: "#fff",
  fontSize: "1rem",
};

const ResidentInfoForm = ({ userInfo }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const notify = useNotification();

  const [editMode, setEditMode] = useState(false);
  const [openHomePicUploadModel, setOpenHomePicUploadModel] = useState(false);

  const homePictures = useSelector((state) => state.user.homeImages);
  const currentAddressObj = useSelector((state) => state.user.userAddressDetails);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      addressLine: currentAddressObj?.addressLine || "",
      landmark: currentAddressObj?.landmark || "",
      city: currentAddressObj?.city || "",
      state: currentAddressObj?.state || "",
      pincode: currentAddressObj?.pincode || "",
      country: currentAddressObj?.country || ""
    }
  });

  const onSubmit = async (address) => {
    try {
      const coordinates = await getGeoLocationFromAddress(address);
      const res = await dispatch(
        SaveUserAddressObject({
          currentLocation: address,
          coordinates
        })
      );
      notify[res.payload.status === "success" ? "success" : "error"](res.payload.message);
      setEditMode(false);
    } catch {
      notify.error("Failed to update address");
    }
  };

  const handleResidentTabUploadFile = async (e, files, setOpen) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("homeImages", file);
    });
    setOpenHomePicUploadModel(false);
    try {
      const res = await dispatch(uploadPostPicture(formData));
      notify[res.payload.status === "success" ? "success" : "error"](res.payload.message);
    } catch {
      notify.error("Failed to upload images");
    }
  };

  const fields = [
    { name: "addressLine", label: "Address Line", required: true },
    { name: "landmark", label: "Landmark", required: false },
    { name: "city", label: "City", required: true },
    { name: "state", label: "State", required: true },
    { name: "pincode", label: "Pincode", required: true },
    { name: "country", label: "Country", required: true }
  ];

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {!editMode ? (
          <Button
            startIcon={<Edit />}
            onClick={() => setEditMode(true)}
            sx={{
              color: "#fff",
              fontSize: "0.85rem",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Edit
          </Button>
        ) : (
          <Button
            startIcon={<Cancel />}
            onClick={() => {
              reset();
              setEditMode(false);
            }}
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontSize: "0.85rem",
              mr: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>

      {fields.map((field) => (
        <TextField
          key={field.name}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false
          })}
          label={field.label}
          variant="outlined"
          fullWidth
          margin="normal"
          sx={!editMode ? readonlyFieldSx : undefined}
          InputProps={{
            readOnly: !editMode,
            sx: inputSx
          }}
          InputLabelProps={{ sx: labelSx }}
          inputProps={{ tabIndex: editMode ? 0 : -1 }}
          error={!!errors[field.name]}
          helperText={errors[field.name]?.message}
          FormHelperTextProps={{
            sx: { color: "rgba(255,200,200,0.9)" }
          }}
        />
      ))}

      {editMode && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            sx={{
              fontSize: "0.85rem",
              backgroundColor: "#fff",
              color: "#001d3d",
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
          >
            Save Changes
          </Button>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 2, color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>
        Home Photos
      </Typography>

      {homePictures?.length > 0 ? (
        <ImageSlider images={homePictures} />
      ) : (
        <EmptyState
          icon={<CloudUpload sx={{ fontSize: 50, color: theme.palette.primary.light }} />}
          title="No home pictures available"
          description="Upload pictures of your home"
        />
      )}

      {editMode && (
        <Button
          variant="contained"
          fullWidth
          startIcon={<CloudUpload />}
          onClick={() => setOpenHomePicUploadModel(true)}
          sx={{
            mt: 3,
            fontSize: "0.85rem",
            backgroundColor: "#fff",
            color: "#001d3d",
            "&:hover": { backgroundColor: "#e0e0e0" },
          }}
        >
          Upload Home Photos
        </Button>
      )}

      <FileUploadModel
        open={openHomePicUploadModel}
        setOpen={setOpenHomePicUploadModel}
        fileUploadProps={{ maxNumber: 4 }}
        handleUploadFile={handleResidentTabUploadFile}
      />
    </Box>
  );
};

export default ResidentInfoForm;
